/**
 * Authentication provider and hook.
 * Manages the user's authentication state by checking the session
 * cookie with the BFF on mount. Provides login/logout helpers and
 * automatic session refresh before expiration.
 *
 * @module dataProviders/AuthProvider
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import apiClient            from '../procedural/apiClient';
import config               from '../config';
import { CLOCK_SKEW_BUFFER_S } from '../constants';

/** @type {React.Context} */
const AuthContext = createContext(null);

/**
 * Provides auth state to child components.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - Child components.
 * @returns {React.ReactElement}
 */
export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef       = useRef(null);
  const wasAuthenticatedRef   = useRef(false);

  /**
   * Cancel any pending refresh timer.
   */
  const cancelRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  /**
   * Schedule a session refresh to fire before the token expires.
   * Accounts for clock skew by refreshing CLOCK_SKEW_BUFFER_S seconds
   * before the server-reported TTL.
   *
   * @param {number} sessionTTL - Session lifetime in seconds (from server).
   */
  const scheduleRefresh = useCallback((sessionTTL) => {
    cancelRefresh();

    const delayMs = (sessionTTL - CLOCK_SKEW_BUFFER_S) * 1000;
    if (delayMs <= 0) return;

    refreshTimerRef.current = setTimeout(async () => {
      try {
        const res = await apiClient.post('/auth/refresh-session');
        scheduleRefresh(res.data.sessionTTL);
      } catch {
        setUser(null);
      }
    }, delayMs);
  }, [cancelRefresh]);

  /**
   * Check if the user has a valid session by querying the BFF.
   * On success, start the refresh timer.
   */
  useEffect(() => {
    apiClient.get('/api/settings')
      .then((res) => {
        setUser(res.data.user);
        scheduleRefresh(res.data.sessionTTL);
      })
      .catch(() => {
        setUser(null);
      })
      .finally(() => {
        setLoading(false);
      });

    return cancelRefresh;
  }, [scheduleRefresh, cancelRefresh]);

  /**
   * Redirect to the home page when an authenticated session expires.
   * Tracks whether the user was previously authenticated so that
   * the initial unauthenticated-on-load case does not trigger a redirect.
   */
  useEffect(() => {
    if (loading) return;
    if (user) {
      wasAuthenticatedRef.current = true;
    } else if (wasAuthenticatedRef.current) {
      wasAuthenticatedRef.current = false;
      window.location.replace(config.pagePaths.home);
    }
  }, [user, loading]);

  /**
   * Log the user out by clearing the session cookie and refresh timer.
   *
   * @returns {Promise<void>}
   */
  const logout = useCallback(async () => {
    cancelRefresh();
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // Ignore errors; the cookie is cleared server-side.
    }
    setUser(null);
  }, [cancelRefresh]);

  const value = {
    isAuthenticated:  !!user,
    user,
    loading,
    setUser,
    scheduleRefresh,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook for accessing the auth context.
 *
 * @returns {{ isAuthenticated: boolean, user: object|null, loading: boolean, setUser: Function, scheduleRefresh: Function, logout: Function }}
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
